import {
  booleanArrayValues,
  FunctionMap,
  isObject,
  mapValues,
  TFunction,
  wrapInCurly,
} from "./utils"
import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { renderFn, get, tokenize } from "micromustache"

/**
 * Deserializer is a class populating serialized strings defined in flow schema with dynamic data
 * while applying string interpolation.
 *
 * For example:
 * deserializer.deserialize('Hi ${flowValues.name}')
 *
 * Could return:
 * "Hi John"
 *
 * For simple values, it won't perform string interpolation and return data in original format
 * For example:
 * deserializer.deserialize('${globalGetters.age}')
 *
 * Could return:
 * 43 // a number
 *
 * Deserializer takes query objects as parameter, to facilitate observing deserialization progress
 * e.g. using:
 *
 * deserializer.aggregator.isLoading
 */
export type OutputType = object | number | string | boolean | null
interface IInterpolationConfig {
  tags: [string, string]
}
const interpolationTags: IInterpolationConfig = {
  tags: ["${", "}"],
}
const mapperInterpolationTags: IInterpolationConfig = { tags: ["<", ">"] }

const getValueFromQuery = (
  path: string,
  queryPath: string,
  query: TFunction,
) => {
  const result = query().data
  const isValueInsideQueryData = path !== queryPath
  if (isValueInsideQueryData) {
    const childPath = path.split(queryPath)[1].substring(1)
    return get(result, childPath)
  }
  return result
}

const getQueryKeyForPath = (path: string, queries: FunctionMap) =>
  Object.keys(queries).find((key) => path.startsWith(key))

class StringInterpolator {
  constructor(
    public getters: object,
    private queries: FunctionMap,
    private mapperFunctions: FunctionMap,
  ) {}

  interpolate(rawString: string) {
    return renderFn(
      rawString,
      this.#applyMappersAndResolve.bind(this),
      this.getters,
      interpolationTags,
    )
  }

  extractVariables(rawString: string) {
    return tokenize(rawString, interpolationTags).varNames
  }

  #findMappedVariable(path: string, scope?: object) {
    let nestedPath
    const mapperName = renderFn(
      path,
      (path) => {
        nestedPath = path
        return ""
      },
      scope,
      mapperInterpolationTags,
    )
    return { nestedPath, mapperName }
  }

  #applyMappersAndResolve(path: string, scope?: object) {
    const { nestedPath, mapperName } = this.#findMappedVariable(path, scope)
    if (mapperName && nestedPath) {
      const value = this.#resolveValue(nestedPath, scope)
      return this.mapperFunctions[mapperName](value)
    }

    return this.#resolveValue(path, scope)
  }

  #resolveValue(path: string, scope?: object) {
    const queryKey = getQueryKeyForPath(path, this.queries)
    return queryKey
      ? getValueFromQuery(path, queryKey, this.queries[queryKey])
      : scope && get(scope, path)
  }
}

export class Deserializer {
  actions: FunctionMap
  getterQueries: FunctionMap
  aggregator: QueryAggregator<unknown>
  stringInterpolator: StringInterpolator

  constructor(
    getters: object,
    getterQueries: FunctionMap,
    mapperFunctions: FunctionMap,
    actions: FunctionMap,
  ) {
    this.actions = actions
    this.getterQueries = getterQueries
    this.aggregator = new QueryAggregator()
    this.stringInterpolator = new StringInterpolator(
      getters,
      getterQueries,
      mapperFunctions,
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deserialize(rawValue?: any): OutputType {
    if (typeof rawValue === "boolean") {
      return rawValue
    }

    if (!rawValue) return null

    if (isObject(rawValue)) {
      return mapValues(rawValue, (value) => this.deserialize(value))
    }

    if (Array.isArray(rawValue)) {
      return rawValue.map((o) =>
        mapValues(o, (value) => this.deserialize(value)),
      )
    }

    this.#getRequiredQueries(rawValue).forEach((query) =>
      this.aggregator.add(query),
    )

    return this.#parseAndDeserialize(rawValue)
  }

  deserializeAction(actionKey: string): TFunction {
    return this.actions[actionKey]
  }

  async deserializeAsync(rawString: string) {
    const allQueries = this.#getRequiredQueries(rawString)
    const allQueryPromises = allQueries.map((query) => query.suspense())
    await Promise.all(allQueryPromises)

    return this.#parseAndDeserialize(rawString)
  }

  #getRequiredQueries(rawString: string) {
    return Object.keys(this.getterQueries)
      .filter((key) => rawString.includes(key))
      .map((key) => {
        return this.getterQueries[key]()
      })
  }

  #parseAndDeserialize(rawString: string) {
    const variablesFound = this.stringInterpolator.extractVariables(rawString)
    const canSkipInterpolation =
      variablesFound.length === 1 &&
      rawString === wrapInCurly(variablesFound[0])

    if (canSkipInterpolation) {
      const path = variablesFound[0]
      const queryKey = getQueryKeyForPath(path, this.getterQueries)

      if (queryKey) {
        return getValueFromQuery(path, queryKey, this.getterQueries[queryKey])
      }
    }

    const interpolatorResult = this.stringInterpolator.interpolate(rawString)

    if (booleanArrayValues.includes(interpolatorResult)) {
      return interpolatorResult === booleanArrayValues[0]
    }

    return this.stringInterpolator.interpolate(rawString)
  }
}
